import { applyPatch, diffWordsWithSpace, parsePatch, reversePatch } from 'diff';
import { toLocaleDateString } from './dates';

export const MARKDOWN_DIFF_LINE_TYPES = {
  CONTEXT: 'context',
  ADDED: 'added',
  REMOVED: 'removed',
  MODIFIED: 'modified',
};

// Renderers consume these markers after markdown parsing, so markdown syntax stays intact.
export const MARKDOWN_DIFF_MARKERS = {
  ADDED_START: '%%DIFF_ADDED%%',
  REMOVED_START: '%%DIFF_REMOVED%%',
  END: '%%DIFF_END%%',
};

const INLINE_CHANGE_MIN_SIMILARITY = 0.55;
const INLINE_CHANGE_MAX_CHANGED_RATIO = 0.45;
const MARKDOWN_DIFF_MARKER_ENTRIES = [
  {
    marker: MARKDOWN_DIFF_MARKERS.ADDED_START,
    type: MARKDOWN_DIFF_LINE_TYPES.ADDED,
  },
  {
    marker: MARKDOWN_DIFF_MARKERS.REMOVED_START,
    type: MARKDOWN_DIFF_LINE_TYPES.REMOVED,
  },
  {
    marker: MARKDOWN_DIFF_MARKERS.END,
    type: null,
  },
];

const getLineType = (line) => {
  if (line.startsWith('+')) {
    return MARKDOWN_DIFF_LINE_TYPES.ADDED;
  }

  if (line.startsWith('-')) {
    return MARKDOWN_DIFF_LINE_TYPES.REMOVED;
  }

  return MARKDOWN_DIFF_LINE_TYPES.CONTEXT;
};

const getDefaultChangeLabel = (version, index) => {
  const changeDate = version.dateInactive || version.dateActive;

  return changeDate ? toLocaleDateString(changeDate) : `Change ${index + 1}`;
};

const getNextDiffMarker = (text) =>
  MARKDOWN_DIFF_MARKER_ENTRIES.reduce((nextMarker, markerData) => {
    const index = text.indexOf(markerData.marker);

    if (index === -1 || (nextMarker && index >= nextMarker.index)) {
      return nextMarker;
    }

    return {
      ...markerData,
      index,
    };
  }, null);

export const getMarkdownDiffTextParts = (text, initialType = null) => {
  const parts = [];
  let rest = text;
  let activeType = initialType;

  while (rest) {
    const nextMarker = getNextDiffMarker(rest);

    if (!nextMarker) {
      parts.push({
        type: activeType,
        content: rest,
      });
      break;
    }

    if (nextMarker.index > 0) {
      parts.push({
        type: activeType,
        content: rest.slice(0, nextMarker.index),
      });
    }

    activeType = nextMarker.type;
    rest = rest.slice(nextMarker.index + nextMarker.marker.length);
  }

  return {
    parts,
    nextType: activeType,
  };
};

const getMarkdownPrefix = (content) => {
  const headingMatch = content.match(/^(\s{0,3}#{1,6}\s+)/);
  if (headingMatch) {
    return {
      marker: headingMatch[0],
      type: `heading-${headingMatch[0].trim().length}`,
    };
  }

  const unorderedListMatch = content.match(/^(\s*)[-*+]\s+/);
  if (unorderedListMatch) {
    return {
      marker: unorderedListMatch[0],
      type: 'unordered-list',
    };
  }

  const orderedListMatch = content.match(/^(\s*\d+\.)\s+/);
  if (orderedListMatch) {
    return {
      marker: orderedListMatch[0],
      type: 'ordered-list',
    };
  }

  return null;
};

const stripSharedMarkdownPrefix = (removedContent, addedContent) => {
  const removedPrefix = getMarkdownPrefix(removedContent);
  const addedPrefix = getMarkdownPrefix(addedContent);

  if (!removedPrefix || removedPrefix.type !== addedPrefix?.type) {
    return {
      removedContent,
      addedContent,
      markdownPrefix: '',
    };
  }

  return {
    removedContent: removedContent.slice(removedPrefix.marker.length),
    addedContent: addedContent.slice(addedPrefix.marker.length),
    markdownPrefix: addedPrefix.marker,
  };
};

const getInlineChangeParts = (removedLineContent, addedLineContent) => {
  const { removedContent, addedContent, markdownPrefix } =
    stripSharedMarkdownPrefix(removedLineContent, addedLineContent);
  const diffParts = diffWordsWithSpace(removedContent, addedContent);
  const changedLength = diffParts.reduce((total, part) => {
    if (part.added || part.removed) {
      return total + part.value.length;
    }

    return total;
  }, 0);
  const unchangedLength = diffParts.reduce((total, part) => {
    if (!part.added && !part.removed) {
      return total + part.value.length;
    }

    return total;
  }, 0);
  const longestLineLength = Math.max(
    removedLineContent.length,
    addedLineContent.length
  );
  const totalLineLength = removedContent.length + addedContent.length;

  if (!longestLineLength || !totalLineLength) {
    return null;
  }

  const similarity = unchangedLength / longestLineLength;
  const changedRatio = changedLength / totalLineLength;

  if (
    similarity < INLINE_CHANGE_MIN_SIMILARITY ||
    changedRatio > INLINE_CHANGE_MAX_CHANGED_RATIO
  ) {
    return null;
  }

  const changedParts = diffParts
    .map((part) => ({
      type: part.added
        ? MARKDOWN_DIFF_LINE_TYPES.ADDED
        : part.removed
          ? MARKDOWN_DIFF_LINE_TYPES.REMOVED
          : MARKDOWN_DIFF_LINE_TYPES.CONTEXT,
      content: part.value,
    }))
    .filter((part) => part.content);

  if (
    !changedParts.some(
      (part) => part.type !== MARKDOWN_DIFF_LINE_TYPES.CONTEXT
    )
  ) {
    return null;
  }

  return {
    markdownPrefix,
    parts: changedParts,
  };
};

const collapseInlineChanges = (lines) => {
  const collapsedLines = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const nextLine = lines[index + 1];

    if (
      line.type === MARKDOWN_DIFF_LINE_TYPES.REMOVED &&
      nextLine?.type === MARKDOWN_DIFF_LINE_TYPES.ADDED
    ) {
      const inlineChange = getInlineChangeParts(line.content, nextLine.content);

      if (inlineChange) {
        collapsedLines.push({
          type: MARKDOWN_DIFF_LINE_TYPES.MODIFIED,
          ...inlineChange,
        });
        index += 1;
        continue;
      }
    }

    collapsedLines.push(line);
  }

  return collapsedLines;
};

const splitMarkdownLines = (markdown) => {
  const normalizedMarkdown = (markdown || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  return normalizedMarkdown.endsWith('\n')
    ? normalizedMarkdown.slice(0, -1).split('\n')
    : normalizedMarkdown.split('\n');
};

const getPatchLines = (patch) =>
  parsePatch(patch).flatMap((filePatch) =>
    filePatch.hunks.flatMap((hunk) =>
      hunk.lines
        .filter((line) => !line.startsWith('\\'))
        .map((line) => ({
          type: getLineType(line),
          content: line.slice(1),
        }))
        .filter((line) => line.content.trim())
    )
  );

export const parseUnifiedPatch = (patch) => {
  if (!patch || typeof patch !== 'string') {
    return [];
  }

  try {
    return collapseInlineChanges(getPatchLines(patch));
  } catch {
    return [];
  }
};

const getAnnotatedMarkdownLines = (markdown, patch) => {
  if (!markdown || !patch) {
    return parseUnifiedPatch(patch);
  }

  try {
    const markdownLines = splitMarkdownLines(markdown);
    const filePatches = parsePatch(patch);
    const lines = [];
    let cursor = 1;

    filePatches.forEach((filePatch) => {
      filePatch.hunks.forEach((hunk) => {
        while (cursor < hunk.newStart) {
          lines.push({
            type: MARKDOWN_DIFF_LINE_TYPES.CONTEXT,
            content: markdownLines[cursor - 1] || '',
          });
          cursor += 1;
        }

        const hunkLines = hunk.lines
          .filter((line) => !line.startsWith('\\'))
          .map((line) => ({
            type: getLineType(line),
            content: line.slice(1),
          }));

        lines.push(...collapseInlineChanges(hunkLines));
        cursor = hunk.newStart + hunk.newLines;
      });
    });

    while (cursor <= markdownLines.length) {
      lines.push({
        type: MARKDOWN_DIFF_LINE_TYPES.CONTEXT,
        content: markdownLines[cursor - 1] || '',
      });
      cursor += 1;
    }

    return lines;
  } catch {
    return parseUnifiedPatch(patch);
  }
};

const getVersionMarkdownDocuments = (versions, currentMarkdown) => {
  if (!currentMarkdown) {
    return [];
  }

  const versionMarkdownDocuments = [];
  let markdown = currentMarkdown;

  for (let index = versions.length - 1; index >= 0; index -= 1) {
    const version = versions[index];
    versionMarkdownDocuments[index] = markdown;

    try {
      const filePatch = parsePatch(version.patch)[0];
      const previousMarkdown = applyPatch(markdown, reversePatch(filePatch));
      if (previousMarkdown !== false) {
        markdown = previousMarkdown;
      }
    } catch {
      // Keep the latest known document for earlier versions if one patch cannot be reversed.
    }
  }

  return versionMarkdownDocuments;
};

const markDiffText = (content, type) => {
  const startMarker =
    type === MARKDOWN_DIFF_LINE_TYPES.ADDED
      ? MARKDOWN_DIFF_MARKERS.ADDED_START
      : MARKDOWN_DIFF_MARKERS.REMOVED_START;

  return `${startMarker}${content}${MARKDOWN_DIFF_MARKERS.END}`;
};

const markMarkdownLine = (content, type) => {
  const prefix = getMarkdownPrefix(content);

  if (!prefix) {
    return markDiffText(content, type);
  }

  return `${prefix.marker}${markDiffText(
    content.slice(prefix.marker.length),
    type
  )}`;
};

const getModifiedMarkdownLine = (line) =>
  `${line.markdownPrefix || ''}${line.parts
    .map((part) =>
      part.type === MARKDOWN_DIFF_LINE_TYPES.CONTEXT
        ? part.content
        : markDiffText(part.content, part.type)
    )
    .join('')}`;

const getLineMarkdown = (line) => {
  if (line.type === MARKDOWN_DIFF_LINE_TYPES.MODIFIED) {
    return getModifiedMarkdownLine(line);
  }

  if (!line.content?.trim()) {
    return '';
  }

  if (
    line.type === MARKDOWN_DIFF_LINE_TYPES.ADDED ||
    line.type === MARKDOWN_DIFF_LINE_TYPES.REMOVED
  ) {
    return markMarkdownLine(line.content, line.type);
  }

  return line.content;
};

const getAnnotatedMarkdown = (lines = []) =>
  lines.map((line) => getLineMarkdown(line)).join('\n');

export const getMarkdownDiffChangeViews = (
  markdownChanges,
  currentMarkdown,
  options = {}
) => {
  const {
    combinedLabel = 'All changes',
    getChangeLabel,
    newestFirst = true,
  } = options;
  const versions = Array.isArray(markdownChanges?.versions)
    ? markdownChanges.versions
    : [];

  if (!versions.length) {
    return [];
  }

  const versionMarkdownDocuments = getVersionMarkdownDocuments(
    versions,
    currentMarkdown
  );
  let views = versions
    .map((version, index) => ({
      version,
      index,
    }))
    .filter(({ version }) => typeof version?.patch === 'string' && version.patch)
    .map(({ version, index }) => ({
      type: 'version',
      label: getChangeLabel
        ? getChangeLabel(version, index)
        : getDefaultChangeLabel(version, index),
      ...version,
      lines: versionMarkdownDocuments[index]
        ? getAnnotatedMarkdownLines(
            versionMarkdownDocuments[index],
            version.patch
          )
        : parseUnifiedPatch(version.patch),
    }))
    .map((view) => ({
      ...view,
      markdown: getAnnotatedMarkdown(view.lines),
    }));

  if (newestFirst) {
    views = views.reverse();
  }

  const combined = markdownChanges?.combined;
  if (combined?.patch) {
    views.push({
      type: 'combined',
      label: combinedLabel,
      ...combined,
      lines: currentMarkdown
        ? getAnnotatedMarkdownLines(currentMarkdown, combined.patch)
        : parseUnifiedPatch(combined.patch),
    });
    views[views.length - 1].markdown = getAnnotatedMarkdown(
      views[views.length - 1].lines
    );
  }

  return views;
};
